import { createFileRoute, useSearch } from "@tanstack/react-router";
import { PageLayout } from "../components/PageLayout";
import { CheckCircle, Loader2, XCircle } from "lucide-react";
import { Button } from "@patient-watch/ui";
import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabaseClient } from "../utils/supabase";

export const Route = createFileRoute("/unsubscribe")({
    component: RouteComponent,
    validateSearch: (search: Record<string, unknown>) => {
        return {
            profile_id: search.profile_id as string,
        };
    },
});

function ProfileComponent() {
    const { profile_id } = useSearch({ from: "/unsubscribe" });
    const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
    const [errorMessage, setErrorMessage] = useState<string>("");

    useEffect(() => {
        if (!profile_id) {
            setStatus("error");
            setErrorMessage("Missing profile ID");
            return;
        }

        const unsubscribe = async () => {
            try {
                const { error } = await supabaseClient.functions.invoke("unsubscribe-sms", {
                    body: { profile_id },
                });

                if (error) {
                    setStatus("error");
                    setErrorMessage(error.message || "Failed to unsubscribe");
                } else {
                    setStatus("success");
                }
            } catch (error) {
                setStatus("error");
                setErrorMessage(error instanceof Error ? error.message : "An unexpected error occurred");
            }
        };

        unsubscribe();
    }, [profile_id]);

    if (status === "loading") {
        return (
            <PageLayout
                title="Unsubscribing..."
                description="Please wait while we process your request"
                icon={Loader2}
                theme="blue"
            >
                <div className="flex flex-col justify-center items-center gap-4">
                    <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                </div>
            </PageLayout>
        );
    }

    if (status === "error") {
        return (
            <PageLayout title="Error" description={errorMessage || "Something went wrong"} icon={XCircle} theme="red">
                <div className="flex flex-col justify-center items-center gap-4">
                    <p className="text-red-600">{errorMessage}</p>
                    <Button asChild variant="default">
                        <Link to="/">Back to Home</Link>
                    </Button>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout
            title="Unsubscribed"
            description="You have been successfully unsubscribed from SMS notifications"
            icon={CheckCircle}
            theme="green"
        >
            <div className="flex flex-col justify-center items-center gap-4">
                <p className="text-gray-600 text-center">
                    You will no longer receive daily reminder messages for the SeaGM Study.
                </p>
                <Button asChild variant="default">
                    <Link to="/">Back to Home</Link>
                </Button>
            </div>
        </PageLayout>
    );
}

function RouteComponent() {
    return <ProfileComponent />;
}
